import { motion, useScroll, useTransform } from "framer-motion";
import { Calendar, MapPin, ChevronDown } from "lucide-react";
import LogoMark from "@/components/LogoMark";

const Line = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Divider = ({ delay = 0 }) => (
  <motion.span
    className="block h-[2px] w-14 my-5"
    style={{ background: "linear-gradient(90deg, transparent, var(--gold), transparent)" }}
    initial={{ scaleX: 0 }}
    animate={{ scaleX: 1 }}
    transition={{ duration: 0.8, delay }}
  />
);

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 160]);
  const opacity = useTransform(scrollY, [0, 550], [1, 0.1]);

  return (
    <header data-testid="hero" className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 py-24">
      <motion.div style={{ y, opacity }} className="flex flex-col items-center max-w-3xl">
        <motion.div
          initial={{ scale: 0.3, opacity: 0, rotate: -14 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <LogoMark className="w-24 h-24 md:w-28 md:h-28" />
        </motion.div>

        <Line delay={0.3} className="mt-7">
          <span className="text-sm md:text-base tracking-[0.55em] pl-[0.55em] font-medium">SPORTS SWEAT</span>
        </Line>
        <Divider delay={0.45} />
        <Line delay={0.5}>
          <span className="text-xs md:text-sm tracking-[0.45em] pl-[0.45em]" style={{ color: "var(--muted)" }}>
            CONVITE ESPECIAL
          </span>
        </Line>
        <Line delay={0.65}>
          <h1 data-testid="hero-title" className="font-display gold-text text-5xl sm:text-7xl lg:text-8xl font-semibold leading-[1.08] my-4">
            INAUGURAÇÃO
          </h1>
        </Line>
        <Line delay={0.8}>
          <span className="text-sm md:text-base tracking-[0.55em] pl-[0.55em]">SPORTS SWEAT</span>
        </Line>

        <Line delay={0.95} className="mt-8">
          <p className="text-base md:text-lg font-light max-w-md">
            É com muita alegria que te convido para a inauguração da minha marca.
          </p>
        </Line>
        <Divider delay={1.05} />
        <Line delay={1.1}>
          <p className="text-xs md:text-sm tracking-[0.3em] leading-loose uppercase" style={{ color: "var(--muted)" }}>
            Será um momento especial
            <br />
            e quero compartilhar com você
            <br />
            esse novo ciclo!
          </p>
        </Line>

        <motion.div
          data-testid="hero-event-card"
          className="lux-card mt-10 px-6 py-6 md:px-10 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-6 sm:gap-10 items-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex sm:flex-col items-center gap-3 text-left sm:text-center">
            <Calendar className="w-6 h-6 shrink-0" style={{ color: "var(--gold)" }} />
            <div>
              <p className="text-sm md:text-base tracking-[0.2em] font-medium">
                31 DE OUTUBRO
                <br className="hidden sm:block" /> DE 2026
              </p>
              <p className="text-xs tracking-[0.25em] mt-1" style={{ color: "var(--muted)" }}>
                A PARTIR DAS 15H
              </p>
            </div>
          </div>
          <div
            className="hidden sm:block w-px self-stretch"
            style={{ background: "linear-gradient(180deg, transparent, var(--gold), transparent)" }}
          />
          <div className="flex sm:flex-col items-center gap-3 text-left sm:text-center">
            <MapPin className="w-6 h-6 shrink-0" style={{ color: "var(--gold)" }} />
            <div>
              <p className="text-sm md:text-base tracking-[0.15em] font-medium uppercase">
                Área de Lazer do
                <br className="hidden sm:block" /> Condomínio Meridian
              </p>
              <p className="text-xs tracking-[0.25em] mt-1" style={{ color: "var(--muted)" }}>
                GOIANÉSIA - GOIÁS
              </p>
            </div>
          </div>
        </motion.div>

        <Line delay={1.45} className="mt-10">
          <span className="text-sm md:text-base tracking-[0.55em] pl-[0.55em] font-medium">ESPERO VOCÊ!</span>
        </Line>
      </motion.div>

      <motion.a
        href="#confirmar"
        data-testid="hero-rsvp-cta"
        className="lux-btn mt-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.6 }}
        onClick={(e) => {
          e.preventDefault();
          document.getElementById("confirmar")?.scrollIntoView({ behavior: "smooth" });
        }}
      >
        Confirmar presença <ChevronDown className="w-4 h-4" />
      </motion.a>
    </header>
  );
}
